import * as RadixTooltip from '@radix-ui/react-tooltip';
import { ReactNode, useRef, useState } from 'react';

/**
 * An ⓘ that explains something, on hover and on tap.
 *
 * Deliberately not the shared Tooltip: that one opens on hover and focus only,
 * which is desktop-only behaviour, and an explanation of why a number looks
 * wrong is exactly what somebody reaches for on a phone. Here a tap pins the
 * content open until it is dismissed, because on touch there is no hover to
 * hold it.
 *
 * The trigger is a real button rather than an icon, so it is reachable by
 * keyboard and announces itself; the content is plain text, so it never needs
 * to take focus.
 */

export interface InfoNoteProps {
  /** Accessible name for the trigger — what this explains. */
  label: string;
  children: ReactNode;
  className?: string;
}

export function InfoNote({ label, children, className }: InfoNoteProps) {
  const [hovering, setHovering] = useState(false);
  // A tap has no hover to keep the content up, so it latches instead.
  const [pinned, setPinned] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  return (
    <RadixTooltip.Provider delayDuration={200}>
      <RadixTooltip.Root
        open={pinned || hovering}
        // Only hover is Radix's to control. It closes on the trigger's own
        // pointerdown, which lands before the click that pins — so letting it
        // clear `pinned` here means every tap re-pins and the note can never
        // be tapped shut. Unpinning is the job of the click, Escape, and a
        // pointer down outside.
        onOpenChange={setHovering}
      >
        <RadixTooltip.Trigger asChild>
          <button
            ref={triggerRef}
            type="button"
            aria-label={label}
            className={`inline-flex items-center justify-center rounded-full text-light-secondaryText hover:text-light-primaryText focus:outline-none focus-visible:ring-2 focus-visible:ring-light-primary dark:text-dark-secondaryText dark:hover:text-dark-primaryText ${
              className ?? ''
            }`}
            // Click rather than pointer events: a tap fires click on touch,
            // where Radix's own hover handling does nothing at all.
            onClick={() => setPinned((was) => !was)}
            // Tabbing away should leave nothing hanging open behind you.
            onBlur={() => setPinned(false)}
          >
            <i className="fa-solid fa-circle-info" aria-hidden />
          </button>
        </RadixTooltip.Trigger>
        <RadixTooltip.Portal>
          <RadixTooltip.Content
            side="top"
            align="start"
            sideOffset={6}
            collisionPadding={8}
            // Without this the content vanishes the moment the pointer leaves
            // the icon, which makes anything longer than a line unreadable.
            //
            // The icon itself counts as "outside" this content, so a press on
            // it arrives here first and unpins — and then the click that
            // follows toggles the note straight back open, leaving it stuck.
            // Pressing the icon is the trigger's business; only a press
            // somewhere else is a dismissal.
            onPointerDownOutside={(event) => {
              const target = event.detail.originalEvent.target as Node | null;
              if (target && triggerRef.current?.contains(target)) return;
              setPinned(false);
            }}
            onEscapeKeyDown={() => setPinned(false)}
            className="z-[10000000] max-w-xs rounded-lg bg-white p-3 text-xs leading-relaxed text-light-primaryText shadow-lg dark:bg-dark-secondaryBackground dark:text-dark-primaryText"
          >
            {children}
            <RadixTooltip.Arrow className="fill-white dark:fill-dark-secondaryBackground" />
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
}

export default InfoNote;
